import React, { useState, useEffect, useMemo } from 'react'
import './Document.css'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import MultiSelect from 'react-multi-select-component'
import DatePicker from 'react-datepicker'
import { SmallSpinner } from '../../utilities/spinner/Spinner'
import { validateFileType } from '../../utilities/fileReader/FileReader'

//Bootstrap
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
//Redux
import { createDocument, getProfiles } from '../../redux'
import { useSelector, useDispatch } from 'react-redux'

function DocumentForm(props) {
  const { file, setFile } = props
  const DOCUMENT_FORMAT = ['pdf']
  var tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow = new Date(tomorrow.setHours(0, 0, 0, 0))

  const history = useHistory()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)

  const ui = useSelector((state) => state.ui)
  const profile = useSelector((state) => state.profile)

  const optionRenderer = useMemo(() => {
    const signers = profile.profiles.filter((signer) => signer.id !== user.credentials.id)
    return signers.map((doc) => ({
      label: `${doc.title} ${doc.name} <${doc.position} @ ${doc.location}>`,
      value: doc.id,
      name: `${doc.title} ${doc.name}`,
    }))
  }, [profile.profiles])

  const customStrings = {
    selectSomeItems: 'Signer(s)',
    allItemsAreSelected: 'All signers are selected.',
    selectAll: 'Select All Signers',
    search: 'Search signer',
  }

  const [dueDate, setDueDate] = useState(tomorrow)

  const [validator, setValidator] = useState({
    isSignerFieldFilled: true,
    isTitleFilled: true,
    isInfoFilled: true,
    isFileSelected: true,
  })

  const [receiversId, setReceiversId] = useState([])
  const [title, setTitle] = useState('')
  const [info, setInfo] = useState('')
  const [isConfirmingModalShown, setIsConfirmingModalShown] = useState(false)

  useEffect(() => {
    if (profile.profiles.length === 0) {
      dispatch(getProfiles())
    }
  }, [])

  const handleCloseModal = () => setIsConfirmingModalShown(false)
  const handleShowModal = () => {
    if (checkAllFields() && validateFileType(file, DOCUMENT_FORMAT)) {
      setIsConfirmingModalShown(true)
    }
  }
  const onSelectFile = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setFile(undefined)
      return
    }
    const file = e.target.files[0]
    if (validateFileType(file, DOCUMENT_FORMAT)) {
      setFile(file)
    } else {
      e.target.value = null
      setFile(undefined)
    }
  }

  const checkAllFields = () => {
    const isSignerFieldFilled = receiversId.length !== 0
    const isTitleFilled = title.length !== 0
    const isInfoFilled = info.length !== 0
    const isFileSelected = file !== undefined
    setValidator({
      ...validator,
      isSignerFieldFilled,
      isTitleFilled,
      isInfoFilled,
      isFileSelected,
    })
    return isSignerFieldFilled && isTitleFilled && isInfoFilled && isFileSelected
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    let participants = []
    let receivers = []
    receiversId.forEach((user) => {
      participants.push(user.value)
      receivers.push({
        id: user.value,
        name: user.name,
        signedAt: null,
      })
    })
    participants.push(user.credentials.id) // take in sender id into participant array
    dispatch(
      createDocument(
        {
          title: title.trim(),
          info: info.trim(),
          receivers,
          sender: {
            id: user.credentials.id,
            name: `${user.credentials.title ? user.credentials.title : ''} ${user.credentials.name}`,
          },
          participants,
          totalReceivers: receivers.length,
          totalSignature: 0,
          status: 'Pending',
          dueDate: dueDate.toISOString(),
        },
        file,
        history
      )
    )
  }

  const handleChangeDate = (date) => {
    const chosenDate = new Date(date.setHours(0, 0, 0, 0))
    setDueDate(chosenDate >= tomorrow ? chosenDate : tomorrow)
  }
  const customValueRenderer = (selected, _options) => {
    return selected.length ? selected.map(({ label }) => `✔️${label} `) : null
  }

  let confirmModal = (
    <Modal show={isConfirmingModalShown} backdrop={'static'}>
      <Modal.Header closeButton={false}>
        <Modal.Title>Are you sure you want to create this document?</Modal.Title>
      </Modal.Header>
      <Modal.Body>Make sure all the informations are correct. Changes cannot be made later on.</Modal.Body>
      <Modal.Footer>
        <Button variant='success' onClick={handleSubmit} disabled={ui.loading}>
          <div>Yes{ui.loading && <SmallSpinner />}</div>
        </Button>
        <Button variant='danger' onClick={handleCloseModal} disabled={ui.loading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return (
    <>
      <Form>
        <Form.Group as={Row} controlId='formTo'>
          <Form.Label column sm='1'>
            To:
          </Form.Label>
          <Col sm='11'>
            <MultiSelect
              options={optionRenderer}
              value={receiversId}
              onChange={setReceiversId}
              labelledBy={'Select'}
              overrideStrings={customStrings}
              hasSelectAll={false}
              valueRenderer={customValueRenderer}
              isLoading={profile.loading}
            />
            {validator.isSignerFieldFilled ? <br /> : <span className='error-text'>Please select at least one signer</span>}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId='formFrom'>
          <Form.Label column sm='1'>
            From:
          </Form.Label>
          <Col>
            <Form.Control readOnly defaultValue={user.credentials.name} />
            <br />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId='formTitle'>
          <Form.Label column sm='1'>
            Title:
          </Form.Label>
          <Col sm='11'>
            <Form.Control
              required
              type='text'
              placeholder='Title'
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              maxLength={200}
            />
            {validator.isTitleFilled ? <br /> : <span className='error-text'>Please insert title.</span>}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId='formInfo'>
          <Form.Label column sm='1'>
            Info:
          </Form.Label>
          <Col sm='11'>
            <Form.Control
              as='textarea'
              rows='3'
              placeholder='Information about the document'
              onChange={(e) => setInfo(e.target.value)}
              value={info}
            />
            {validator.isInfoFilled ? <br /> : <span className='error-text'>Please insert info.</span>}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId='formDuedate'>
          <Form.Label column sm='2'>
            Duedate
          </Form.Label>
          <Col>
            <DatePicker selected={dueDate} onChange={handleChangeDate} dateFormat='MMMM d, yyyy 00:00:00'>
              <div style={{ color: 'red' }}>Duedate must be tomorrow and onward!</div>
            </DatePicker>
          </Col>
        </Form.Group>
        <br />
        <Form.Group>
          <Form.File style={{ background: '#e0dfda' }} id='formFile' accept='.pdf' onChange={onSelectFile} />
          {validator.isFileSelected ? <br /> : <span className='error-text'>Please upload a file.</span>}
        </Form.Group>
      </Form>
      <Button variant='success' onClick={handleShowModal}>
        Submit
      </Button>
      {confirmModal}
    </>
  )
}

DocumentForm.propTypes = {
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setFile: PropTypes.func.isRequired,
}

export default DocumentForm
